import React from 'react';
import { Skeleton } from '@mui/material';
import {
  Shield,
  TrendingUp,
  PieChart,
  Map,
  BugReport,
} from '@mui/icons-material';

const formatNumber = (n) => {
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)} L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(1)}K`;
  return `₹${n.toLocaleString('en-IN')}`;
};

const cards = [
  {
    key: 'activePolicies',
    label: 'Active Policies',
    icon: Shield,
    color: 'emerald',
    format: (d) => d.activePolicies,
    sub: (d) => `${d.totalPolicies} total policies`,
  },
  {
    key: 'claimsRatio',
    label: 'Claims Ratio',
    icon: TrendingUp,
    color: 'amber',
    format: (d) =>
      d.totalPolicies > 0
        ? `${((d.totalClaims / d.totalPolicies) * 100).toFixed(1)}%`
        : '0%',
    sub: (d) => `${d.totalClaims} claims on ${d.totalPolicies} policies`,
  },
  {
    key: 'lossRatio',
    label: 'Loss Ratio %',
    icon: PieChart,
    color: 'red',
    format: (d) =>
      d.totalPremium > 0
        ? `${((d.totalClaimAmount / d.totalPremium) * 100).toFixed(1)}%`
        : '0%',
    sub: (d) => `${formatNumber(d.totalClaimAmount)} / ${formatNumber(d.totalPremium)}`,
  },
  {
    key: 'districtExposure',
    label: 'District Exposure',
    icon: Map,
    color: 'blue',
    format: (d) => `${d.distinctDistricts} Districts`,
    sub: (d) => `Top: ${d.topDistrict} (${formatNumber(d.topDistrictExposure)})`,
  },
  {
    key: 'diseaseFreq',
    label: 'Disease Frequency',
    icon: BugReport,
    color: 'purple',
    format: (d) => `${d.diseaseClaimCount} Events`,
    sub: (d) => `Top: ${d.topDisease}`,
  },
];

const colorMap = {
  emerald: { bg: 'bg-emerald-50', icon: 'bg-emerald-100', text: 'text-emerald-600' },
  amber: { bg: 'bg-amber-50', icon: 'bg-amber-100', text: 'text-amber-600' },
  red: { bg: 'bg-red-50', icon: 'bg-red-100', text: 'text-red-600' },
  blue: { bg: 'bg-blue-50', icon: 'bg-blue-100', text: 'text-blue-600' },
  purple: { bg: 'bg-purple-50', icon: 'bg-purple-100', text: 'text-purple-600' },
};

const RiskKPICards = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <Skeleton variant="rounded" height={80} />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
      {cards.map((card) => {
        const c = colorMap[card.color];
        const Icon = card.icon;
        return (
          <div
            key={card.key}
            className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between mb-3">
              <div className={`w-10 h-10 rounded-xl ${c.icon} flex items-center justify-center`}>
                <Icon sx={{ fontSize: 20 }} className={c.text} />
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900 mb-1">{card.format(data)}</p>
            <p className="text-xs font-medium text-gray-500 mb-1">{card.label}</p>
            <p className="text-xs text-gray-400 truncate">{card.sub(data)}</p>
          </div>
        );
      })}
    </div>
  );
};

export default RiskKPICards;
